/**
 * Widget dashboard « Échéances déclaratives » (Lot F) — AUTONOME.
 *
 * Résume les prochaines déclarations à préparer (DRM avant le 10, DS en saison,
 * DR en saison) avec leur compte à rebours et leur état. Pensé pour être déposé
 * tel quel dans le Dashboard (le câblage y est fait à part) : il fait ses propres
 * requêtes via `useEcheancesDeclaratives`, se masque si l'utilisateur n'a aucune
 * permission de lecture conformité, et affiche un message honnête « à jour »
 * plutôt qu'une carte vide.
 *
 * NB : partage ses queryKeys avec le Hub Conformité → react-query dédupe.
 */

import { Card, DatePill, Skeleton, WidgetHeader } from "@champ-erp/ui";
import { CheckCircle2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { useCanAny } from "../../auth/use-can.js";
import { formatDateFr } from "../../lib/format-dates.js";

import { formatJoursRestants } from "./echeances.js";
import {
  useEcheancesDeclaratives,
  type EcheanceDeclarative,
} from "./use-echeances-declaratives.js";
import { BadgeEtatDeclaration, ROUTES_CONFORMITE } from "./utils.js";

export function EcheancesDeclaratives() {
  const navigate = useNavigate();
  // Masquage de confort : sans aucune lecture conformité, on ne rend rien.
  const { granted: peutVoir } = useCanAny(["drm.read", "ds.read", "dr.read"]);
  const { echeances, isLoading } = useEcheancesDeclaratives();

  if (!peutVoir) return null;

  return (
    <Card className="p-6">
      <WidgetHeader
        eyebrow="Conformité · Déclarations"
        title="Mes échéances déclaratives"
        action={{
          label: "Ouvrir la conformité",
          href: ROUTES_CONFORMITE.hub,
          onClick: (event) => {
            event.preventDefault();
            navigate(ROUTES_CONFORMITE.hub);
          },
        }}
      />

      {isLoading ? (
        <div className="space-y-2" aria-busy="true">
          <Skeleton variant="rect" height={52} />
          <Skeleton variant="rect" height={52} />
        </div>
      ) : echeances.length === 0 ? (
        <MessageAJour />
      ) : (
        <ul className="space-y-2">
          {echeances.map((echeance) => (
            <li key={echeance.id}>
              <LigneEcheance echeance={echeance} />
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/** Une échéance : titre + état + date + compte à rebours, cliquable. */
function LigneEcheance({ echeance }: { echeance: EcheanceDeclarative }) {
  return (
    <Link
      to={echeance.route}
      className="flex items-center justify-between gap-3 rounded-xl border border-champagne-200 bg-white p-3 transition hover:border-champagne-300 hover:bg-champagne-50/50"
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-[14px] font-semibold text-aubergine-900">
            {echeance.titre}
          </span>
          <BadgeEtatDeclaration etat={echeance.etat} />
        </div>
        <div className="mt-0.5 text-[12px] text-champagne-700">
          Échéance {formatDateFr(echeance.echeanceIso)}
        </div>
      </div>
      {!echeance.faite && (
        <DatePill urgent={echeance.urgente}>{formatJoursRestants(echeance.jours)}</DatePill>
      )}
    </Link>
  );
}

/** État « à jour » honnête (jamais une carte vide anxiogène). */
function MessageAJour() {
  return (
    <p className="flex items-center gap-2 rounded-lg border border-dashed border-champagne-200 bg-champagne-50/40 px-4 py-6 text-center text-[13px] text-champagne-700">
      <CheckCircle2 size={16} className="shrink-0 text-vine-600" aria-hidden="true" />
      Vous êtes à jour sur vos déclarations.
    </p>
  );
}
